/**
 * UserGuide - Guia Interativo de Uso da Calculadora
 * 
 * Baseado no tripé HeveaTalent:
 * - JUSTIÇA: Dados transparentes e verificáveis
 * - SEGURANÇA: Seus dados são privados
 * - SIMPLICIDADE: Fácil de entender e usar
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useTranslation } from '../../translations';

// Componente de Tooltip com ícone de interrogação
export const InfoTooltip = ({ children, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        className="ml-1 w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 text-xs font-bold inline-flex items-center justify-center transition-colors"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
      >
        ?
      </button>
      {isVisible && (
        <div className={`absolute z-50 w-72 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-xl ${positionClasses[position]}`}>
          <div className="relative">
            {children}
          </div>
        </div>
      )}
    </span>
  );
};

// Componente de Card Explicativo com Tripé
export const TripodCard = ({ type, title, children }) => {
  const { t } = useTranslation();
  
  const configs = {
    justica: {
      icon: '⚖️',
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-300',
      titleColor: 'text-green-800',
      label: t('fairness').toUpperCase()
    },
    seguranca: {
      icon: '🔒',
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
      titleColor: 'text-blue-800',
      label: t('security').toUpperCase()
    },
    simplicidade: {
      icon: '✨',
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-300',
      titleColor: 'text-purple-800',
      label: t('simplicity').toUpperCase()
    }
  };

  const config = configs[type] || configs.simplicidade;

  return (
    <div className={`p-4 rounded-lg ${config.bgColor} border ${config.borderColor}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{config.icon}</span>
        <span className={`text-xs font-bold ${config.titleColor} uppercase`}>{config.label}</span>
      </div>
      {title && <h4 className={`font-bold ${config.titleColor} mb-1`}>{title}</h4>}
      <div className="text-sm text-gray-700">{children}</div>
    </div>
  );
};

// Guia Completo de Uso
const UserGuide = ({ onClose }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [live, setLive] = useState(null);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/market/quotations`)
      .then((r) => r.json())
      .then((d) => {
        const q = {};
        (d.quotations || []).forEach((x) => { q[x.type] = x; });
        setLive(q);
      })
      .catch(() => {});
  }, []);

  const gv = (type, fb) => live?.[type]?.value ?? fb;
  const fmt = (v) => `R$ ${Number(v).toFixed(2).replace('.', ',')}`;
  const geb10 = gv('geb10_apabor', 13.35);
  const geb10Ref = live?.geb10_apabor?.reference_date || '';
  const imp = gv('preco_importacao', 16.37);
  const est = gv('geb10_estimado', null);
  const part = Math.round(gv('participacao_implicita', 72));
  const coe = gv('custo_producao_coe', 5.0);
  const cot = gv('custo_producao_cot', 6.07);
  const intl = gv('internacional', null);
  const cambioLive = gv('cambio', null);
  const importacao = gv('preco_importacao', null);
  const coaguloLive = gv('coagulo', null);
  const calc = (ref, rate) => ref * 0.53 * (rate / 100);
  const marginRates = Array.from(new Set([52, 60, part, 80])).sort((a, b) => a - b);

  const steps = [
    {
      title: t('ug_step1_title'),
      icon: "📚",
      content: (
        <div className="space-y-4">
          <p className="text-lg">
            {t('ug_step1_intro')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <TripodCard type="justica">
              {t('ug_step1_tripod1')}
            </TripodCard>
            <TripodCard type="seguranca">
              {t('ug_step1_tripod2')}
            </TripodCard>
            <TripodCard type="simplicidade">
              {t('ug_step1_tripod3')}
            </TripodCard>
          </div>
        </div>
      )
    },
    {
      title: t('ug_step2_title'),
      icon: "🔗",
      content: (
        <div className="space-y-4">
          <p>{t('ug_step2_intro')}</p>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-purple-100 rounded-lg">
              <span className="text-2xl">🌍</span>
              <div>
                <p className="font-bold text-purple-800">{t('ug_step2_1_title')}</p>
                <p className="text-sm text-gray-600">{t('ug_step2_1_desc')}{intl ? `: ~US$ ${Number(intl).toFixed(2).replace('.', ',')}/kg` : ''}</p>
              </div>
            </div>
            <div className="flex justify-center text-2xl text-gray-400">↓</div>
            <div className="flex items-center gap-3 p-3 bg-blue-100 rounded-lg">
              <span className="text-2xl">💱</span>
              <div>
                <p className="font-bold text-blue-800">{t('ug_step2_2_title')}</p>
                <p className="text-sm text-gray-600">{t('ug_step2_2_desc')}{cambioLive ? ` — PTAX: ${fmt(cambioLive)}` : ''}</p>
              </div>
            </div>
            <div className="flex justify-center text-2xl text-gray-400">↓</div>
            <div className="flex items-center gap-3 p-3 bg-green-100 rounded-lg">
              <span className="text-2xl">📊</span>
              <div>
                <p className="font-bold text-green-800">{t('ug_step2_3_title')}</p>
                <p className="text-sm text-gray-600">{t('ug_step2_3_desc')}: {fmt(geb10)} | {importacao ? fmt(importacao) : '—'}</p>
              </div>
            </div>
            <div className="flex justify-center text-2xl text-gray-400">↓</div>
            <div className="flex items-center gap-3 p-3 bg-orange-100 rounded-lg">
              <span className="text-2xl">🧮</span>
              <div>
                <p className="font-bold text-orange-800">{t('ug_step2_4_title')}</p>
                <p className="text-sm text-gray-600">{t('ug_step2_4_desc')}</p>
              </div>
            </div>
            <div className="flex justify-center text-2xl text-gray-400">↓</div>
            <div className="flex items-center gap-3 p-3 bg-green-200 rounded-lg border-2 border-green-500">
              <span className="text-2xl">💰</span>
              <div>
                <p className="font-bold text-green-800">{t('ug_step2_5_title')}</p>
                <p className="text-sm text-gray-600">{t('ug_step2_5_desc')}{coaguloLive ? `: ~${fmt(coaguloLive)}/kg` : ''}</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: t('ug_step3_title'),
      icon: "📊",
      content: (
        <div className="space-y-4">
          <TripodCard type="justica" title={t('officialReferences')}>
            {t('ug_step3_tripod')}
          </TripodCard>
          
          <p className="font-medium">{t('ug_step3_mainRefs')}</p>
          
          <div className="space-y-3">
            <div className="p-3 bg-green-50 border-2 border-green-400 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-green-800">🏭 GEB-10 APABOR</p>
                    <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">{t('ug_step3_adoptedByMills')}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t('ug_step3_geb10Desc')}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-700">{fmt(geb10)}</p>
                  {geb10Ref && <p className="text-[10px] text-gray-400">{geb10Ref}</p>}
                </div>
              </div>
              <p className="text-xs text-green-600 mt-1">✅ {t('ug_step3_geb10Note')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('ug_step3_bimonthly')}</p>
            </div>
            
            <div className="p-3 bg-blue-50 border border-blue-300 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-bold text-blue-800">📊 {t('importPrice')} (IEA/CNA)</p>
                  <p className="text-sm text-gray-600">{t('ug_step3_ieaDesc')}</p>
                </div>
                <p className="text-2xl font-bold text-blue-700">{fmt(imp)}</p>
              </div>
              <p className="text-xs text-blue-600 mt-1">{t('ug_step3_ieaIncl')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('ug_step3_monthly')}</p>
            </div>
            
            <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-bold text-gray-800">🛰️ GEB-10 Estimado (HeveaTalent)</p>
                  <p className="text-sm text-gray-600">Estimado pela plataforma: TSR-20 do dia × dólar PTAX × fator calibrado</p>
                </div>
                <p className="text-2xl font-bold text-gray-700">{est ? fmt(est) : '—'}</p>
              </div>
              <p className="text-xs text-gray-500 mt-1">Atualizado diariamente pelo Radar</p>
            </div>
          </div>
          
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg">
            <p className="text-amber-800 font-bold">💡 {t('ug_step3_whyGeb10')}</p>
            <p className="text-sm text-amber-700">
              {t('ug_step3_whyGeb10Desc')}
            </p>
          </div>
        </div>
      )
    },
    {
      title: t('ug_step4_title'),
      icon: "🌿",
      content: (
        <div className="space-y-4">
          <TripodCard type="simplicidade" title={t('whatIsDrc')}>
            {t('ug_step4_tripod')}
          </TripodCard>
          
          <div className="p-4 bg-white border rounded-lg">
            <p className="font-bold text-gray-800 mb-3">{t('ug_step4_howWorks')}</p>
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">{t('ug_step4_muchWater')}</span>
              <span className="font-bold text-red-600">{t('ug_step4_lowDrc')}</span>
            </div>
            <div className="h-3 bg-gradient-to-r from-red-400 via-yellow-400 to-green-500 rounded-full mb-2"></div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">{t('ug_step4_wellDried')}</span>
              <span className="font-bold text-green-600">{t('ug_step4_highDrc')}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <p className="text-3xl">💧</p>
              <p className="font-bold text-red-800">DRC 50%</p>
              <p className="text-sm text-gray-600">{t('ug_step4_mayReject')}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg text-center">
              <p className="text-3xl">🌿</p>
              <p className="font-bold text-yellow-800">DRC 53%</p>
              <p className="text-sm text-gray-600">{t('ug_step4_minAccepted')}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-3xl">✅</p>
              <p className="font-bold text-green-800">DRC 55-60%</p>
              <p className="text-sm text-gray-600">{t('ug_step4_goodExcellent')}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-3xl">⭐</p>
              <p className="font-bold text-blue-800">DRC 65%+</p>
              <p className="text-sm text-gray-600">{t('ug_step4_excellent')}</p>
            </div>
          </div>
          
          <div className="p-3 bg-green-100 rounded-lg">
            <p className="text-green-800 font-bold">💡 {t('tip')}:</p>
            <p className="text-sm text-green-700">
              {t('ug_step4_tip')}
            </p>
          </div>
        </div>
      )
    },
    {
      title: t('ug_step5_title'),
      icon: "💰",
      content: (
        <div className="space-y-4">
          <TripodCard type="justica" title={t('pf_participationRateTitle')}>
            {t('ug_step5_tripod')}
          </TripodCard>
          
          <div className="p-4 bg-white border rounded-lg">
            <p className="font-bold text-gray-800 mb-3">{t('ug_step5_ranges')}</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                <span>40-50%</span>
                <span className="text-red-700 font-bold">{t('ug_step5_veryLow')} ❌</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                <span>52%</span>
                <span className="text-yellow-700 font-bold">{t('ug_step5_minCna')} ⚠️</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                <span>60-70%</span>
                <span className="text-green-700 font-bold">{t('ug_step5_good')} ✅</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-green-100 rounded border border-green-400">
                <span>80%</span>
                <span className="text-green-800 font-bold">{t('ug_step5_ieaRef')} ⭐</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                <span>85-88,5%</span>
                <span className="text-blue-700 font-bold">{t('ug_step5_excellentRate')} 🏆</span>
              </div>
            </div>
          </div>
          
          <div className="p-3 bg-orange-100 rounded-lg">
            <p className="text-orange-800 font-bold">⚠️ {t('important')}:</p>
            <p className="text-sm text-orange-700">
              {t('ug_step5_important')}
            </p>
          </div>
          
          <div className="p-3 bg-blue-100 rounded-lg">
            <p className="text-blue-800 font-bold">💡 {t('ug_step5_howToGet')}</p>
            <ul className="text-sm text-blue-700 list-disc pl-5 mt-1">
              <li>{t('ug_step5_tip1')}</li>
              <li>{t('ug_step5_tip2')}</li>
              <li>{t('ug_step5_tip3')}</li>
              <li>{t('ug_step5_tip4')}</li>
              <li>{t('ug_step5_tip5')}</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: t('ug_step6_title'),
      icon: "🧮",
      content: (
        <div className="space-y-4">
          <div className="p-4 bg-green-100 rounded-lg border-2 border-green-400">
            <p className="text-center font-mono text-lg text-green-800">
              <strong>{t('ug_step6_formula')}</strong>
            </p>
          </div>
          
          <p className="font-medium">{t('ug_step6_compare')}</p>
          
          <div className="p-4 bg-white border rounded-lg space-y-3">
            <div className="p-3 bg-green-50 border-2 border-green-400 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">{t('ug_step3_adoptedByMills')}</span>
                <span className="font-bold text-green-800">GEB-10 APABOR</span>
              </div>
              <p className="text-xs text-gray-600 mb-2">
                {t('ug_step6_geb10Ref')}
              </p>
              <div className="font-mono text-sm text-gray-600">
                {fmt(geb10)} × 53% × {part}% =
              </div>
              <div className="text-2xl font-bold text-green-700">
                {fmt(calc(geb10, part))}/kg
              </div>
            </div>
            
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">{t('ug_step3_importParity')}</span>
                <span className="font-bold text-blue-800">{t('importPrice')} IEA</span>
              </div>
              <p className="text-xs text-gray-600 mb-2">
                {t('ug_step6_ieaRef')}
              </p>
              <div className="font-mono text-sm text-gray-600">
                {fmt(imp)} × 53% × 72% =
              </div>
              <div className="text-2xl font-bold text-blue-700">
                {fmt(calc(imp, 72))}/kg
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs bg-gray-500 text-white px-2 py-0.5 rounded">🛰️ Diário</span>
                <span className="font-bold text-gray-800">GEB-10 Estimado</span>
              </div>
              <p className="text-xs text-gray-600 mb-2">
                Estimativa diária da plataforma enquanto o GEB-10 oficial não é publicado
              </p>
              <div className="font-mono text-sm text-gray-600">
                {fmt(est || geb10)} × 53% × {part}% =
              </div>
              <div className="text-2xl font-bold text-gray-700">
                {fmt(calc(est || geb10, part))}/kg
              </div>
            </div>
          </div>
          
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg">
            <p className="text-amber-800 font-bold text-sm mb-2">📊 {t('ug_step6_relation')}</p>
            <div className="text-xs text-amber-700 space-y-1">
              <p>• <strong>{t('importPrice')} IEA ({fmt(imp)})</strong> → {t('ug_step6_maxCost')}</p>
              <p>• <strong>GEB-10 APABOR ({fmt(geb10)})</strong> → {t('ug_step6_refNegotiation')}</p>
              <p>• <strong>GEB-10 Estimado ({est ? fmt(est) : '—'})</strong> → termômetro diário entre publicações oficiais</p>
            </div>
          </div>
          
          <TripodCard type="justica" title={t('understandDifference')}>
            {t('ug_step6_understand')}
          </TripodCard>
        </div>
      )
    },
    {
      title: t('ug_step7_title'),
      icon: "💵",
      content: (
        <div className="space-y-4">
          <TripodCard type="simplicidade" title={t('priceVsCost')}>
            {t('ug_step7_tripod')}
          </TripodCard>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <p className="text-sm text-gray-600">{t('ug_step7_coe')}</p>
              <p className="text-3xl font-bold text-orange-700">{fmt(coe)}</p>
              <p className="text-xs text-gray-500">{t('ug_step7_coeDesc')}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg text-center">
              <p className="text-sm text-gray-600">{t('ug_step7_cot')}</p>
              <p className="text-3xl font-bold text-red-700">{fmt(cot)}</p>
              <p className="text-xs text-gray-500">{t('ug_step7_cotDesc')}</p>
            </div>
          </div>
          
          <div className="p-4 bg-white border rounded-lg">
            <p className="font-bold mb-2">{t('ug_step7_marginAnalysis')}</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">{t('ug_step7_scenario')}</th>
                  <th className="p-2 text-right">{t('ug_step7_price')}</th>
                  <th className="p-2 text-right">{t('ug_step7_marginVsCot')}</th>
                </tr>
              </thead>
              <tbody>
                {marginRates.map((rate) => {
                  const price = calc(geb10, rate);
                  const margin = price - cot;
                  const icon = margin >= 0 ? '✅' : margin >= -1 ? '⚠️' : '❌';
                  return (
                    <tr key={rate} className={`border-b ${rate === part ? 'bg-green-50' : ''}`}>
                      <td className="p-2">Taxa {rate}%, DRC 53%{rate === part ? ' (implícita atual)' : ''}</td>
                      <td className="p-2 text-right">{fmt(price)}</td>
                      <td className={`p-2 text-right font-bold ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {margin >= 0 ? '+' : '-'}{fmt(Math.abs(margin))} {icon}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] text-gray-400 mt-1">Base: GEB-10 {fmt(geb10)} × DRC 53% × taxa · COT {fmt(cot)}</p>
          </div>
          
          <div className="p-3 bg-yellow-100 rounded-lg">
            <p className="text-yellow-800 font-bold">💡 {t('analysis')}:</p>
            <p className="text-sm text-yellow-700">
              {t('ug_step7_analysis')}
            </p>
          </div>
        </div>
      )
    },
    {
      title: t('ug_step8_title'),
      icon: "📋",
      content: (
        <div className="space-y-4">
          <TripodCard type="justica" title={t('verifiableData')}>
            {t('ug_step8_tripod')}
          </TripodCard>
          
          <div className="space-y-3">
            <div className="p-3 bg-white border rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">🌿</span>
                <span className="font-bold">{t('ug_step8_lateks')}</span>
              </div>
              <p className="text-sm text-gray-600">{t('ug_step8_lateksDesc')}</p>
              <a href="https://borrachanatural.agr.br" target="_blank" rel="noopener noreferrer" 
                className="text-xs text-blue-600 hover:underline">
                borrachanatural.agr.br
              </a>
              <p className="text-xs text-green-600 mt-1">✅ {t('ug_step8_lateksNote')}</p>
            </div>
            
            <div className="p-3 bg-white border rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">📊</span>
                <span className="font-bold">{t('ug_step8_iea')}</span>
              </div>
              <p className="text-sm text-gray-600">{t('ug_step8_ieaDesc')}</p>
              <a href="https://iea.agricultura.sp.gov.br" target="_blank" rel="noopener noreferrer" 
                className="text-xs text-blue-600 hover:underline">
                iea.agricultura.sp.gov.br
              </a>
              <p className="text-xs text-gray-400 mt-1">* {t('ug_step8_ieaNote')}</p>
            </div>
            
            <div className="p-3 bg-white border rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">🏭</span>
                <span className="font-bold">{t('ug_step8_apabor')}</span>
              </div>
              <p className="text-sm text-gray-600">{t('ug_step8_apaborDesc')}</p>
              <a href="https://apabor.org.br/boletim-e-notas-tecnicas/" target="_blank" rel="noopener noreferrer" 
                className="text-xs text-blue-600 hover:underline">
                apabor.org.br/boletim-e-notas-tecnicas
              </a>
              <p className="text-xs text-gray-400 mt-1">* {t('ug_step8_apaborNote')}</p>
            </div>
            
            <div className="p-3 bg-white border rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">💰</span>
                <span className="font-bold">{t('ug_step8_noticias')}</span>
              </div>
              <p className="text-sm text-gray-600">{t('ug_step8_noticiasDesc')}</p>
              <a href="https://www.noticiasagricolas.com.br/cotacoes/latex" target="_blank" rel="noopener noreferrer" 
                className="text-xs text-blue-600 hover:underline">
                noticiasagricolas.com.br/cotacoes/latex
              </a>
            </div>
            
            <div className="p-3 bg-white border rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">🌍</span>
                <span className="font-bold">{t('ug_step8_trading')}</span>
              </div>
              <p className="text-sm text-gray-600">{t('ug_step8_tradingDesc')}</p>
              <a href="https://tradingeconomics.com/commodity/rubber" target="_blank" rel="noopener noreferrer" 
                className="text-xs text-blue-600 hover:underline">
                tradingeconomics.com/commodity/rubber
              </a>
            </div>
          </div>
        </div>
      )
    },
    {
      title: t('ug_step9_title'),
      icon: "🤝",
      content: (
        <div className="space-y-4">
          <TripodCard type="simplicidade" title={t('knowledgeIsPower')}>
            {t('ug_step9_tripod')}
          </TripodCard>
          
          <div className="space-y-3">
            <div className="p-3 bg-green-50 rounded-lg flex items-start gap-3">
              <span className="text-2xl">1️⃣</span>
              <div>
                <p className="font-bold text-green-800">{t('ug_step9_tip1_title')}</p>
                <p className="text-sm text-gray-600">
                  {t('ug_step9_tip1_desc')}
                </p>
              </div>
            </div>
            
            <div className="p-3 bg-green-50 rounded-lg flex items-start gap-3">
              <span className="text-2xl">2️⃣</span>
              <div>
                <p className="font-bold text-green-800">{t('ug_step9_tip2_title')}</p>
                <p className="text-sm text-gray-600">
                  {t('ug_step9_tip2_desc')}
                </p>
              </div>
            </div>
            
            <div className="p-3 bg-green-50 rounded-lg flex items-start gap-3">
              <span className="text-2xl">3️⃣</span>
              <div>
                <p className="font-bold text-green-800">{t('ug_step9_tip3_title')}</p>
                <p className="text-sm text-gray-600">
                  {t('ug_step9_tip3_desc')}
                </p>
              </div>
            </div>
            
            <div className="p-3 bg-green-50 rounded-lg flex items-start gap-3">
              <span className="text-2xl">4️⃣</span>
              <div>
                <p className="font-bold text-green-800">{t('ug_step9_tip4_title')}</p>
                <p className="text-sm text-gray-600">
                  {t('ug_step9_tip4_desc')}
                </p>
              </div>
            </div>
            
            <div className="p-3 bg-green-50 rounded-lg flex items-start gap-3">
              <span className="text-2xl">5️⃣</span>
              <div>
                <p className="font-bold text-green-800">{t('ug_step9_tip5_title')}</p>
                <p className="text-sm text-gray-600">
                  {t('ug_step9_tip5_desc')}
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-green-100 rounded-lg border-2 border-green-400 text-center">
            <p className="text-green-800 font-bold text-lg">🎉 {t('ug_step9_congrats')}</p>
            <p className="text-green-700">
              {t('ug_step9_ready')}
            </p>
          </div>
        </div>
      )
    }
  ];

  const currentStepData = steps[currentStep];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <CardHeader className="bg-green-600 text-white">
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <span className="text-2xl">{currentStepData.icon}</span>
              {currentStepData.title}
            </CardTitle>
            <button 
              onClick={onClose}
              className="text-white/80 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>
          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1">
              <span>{t('stepOfTotal').replace('{current}', currentStep + 1).replace('{total}', steps.length)}</span>
              <span>{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
            </div>
            <div className="h-2 bg-green-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 overflow-y-auto max-h-[60vh]">
          {currentStepData.content}
        </CardContent>
        <div className="p-4 border-t bg-gray-50 flex justify-between">
          <button
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
          >
            ← {t('previousBtn')}
          </button>
          <div className="flex gap-1">
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  idx === currentStep ? 'bg-green-600' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
          {currentStep < steps.length - 1 ? (
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              {t('nextBtn')} →
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              {t('startUsingBtn')} 🚀
            </button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default UserGuide;
